import { CONSENT_COOKIE_NAME, TermlyCookieConsentProvider } from "./TermlyCookieConsentProvider";
import type { CookieConsentProvider } from "./types";

export type { CookieConsentProvider } from "./types";
export {
  TermlyCookieConsentProvider,
  CONSENT_COOKIE_NAME,
  SHARED_COOKIE_DOMAIN,
  TERMLY_SCRIPT_URL,
  TERMLY_CONSENT_SYNC_URL,
  DEFAULT_MASTER_CONSENTS_ORIGIN,
  DEFAULT_TERMLY_WEBSITE_ID,
  buildResourceBlockerUrl,
  buildTermlyScriptUrl,
  buildConsentSyncUrl,
  ConsentValue,
} from "./TermlyCookieConsentProvider";

/**
 * A configured environment value, or `undefined` when it is unset or blank.
 *
 * A blank variable must become `undefined` rather than an empty string: the
 * provider falls back to its defaults with `??` and a default parameter, and
 * both of those treat `""` as a deliberate value, which would yield a broken
 * Termly script URL and consent-sync origin.
 */
function readEnv(value: string | undefined): string | undefined {
  const trimmed = value?.trim();

  return trimmed === undefined || trimmed === "" ? undefined : trimmed;
}

const configuredMasterConsentsOrigin = readEnv(import.meta.env.VITE_TERMLY_MASTER_CONSENTS_ORIGIN);
const configuredWebsiteId = readEnv(import.meta.env.VITE_TERMLY_WEBSITE_ID);

/**
 * Name the shared consent cookie is written under in this environment.
 *
 * DesignMyNight prefixes every cookie with its environment outside production
 * (`qa_consent` on QA, `dev_consent` locally), so an unprefixed name only finds
 * the main site's decision in production.
 */
const configuredCookieName = `${readEnv(import.meta.env.VITE_DMN_COOKIE_PREFIX) ?? ""}${CONSENT_COOKIE_NAME}`;

/**
 * The single configured consent provider consumers should depend on.
 *
 * Swapping providers later only requires changing this instance to another
 * {@link CookieConsentProvider} implementation.
 */
const cookieConsent: CookieConsentProvider = new TermlyCookieConsentProvider({
  disabled: import.meta.env.VITE_TERMLY_DISABLED === "true",
  masterConsentsOrigin: configuredMasterConsentsOrigin,
  websiteId: configuredWebsiteId,
  cookieName: configuredCookieName,
});

export default cookieConsent;
